'use client';

import type { MouseEventHandler, RefObject } from 'react';
import { useCallback } from 'react';
import { usePathname, useRouter } from 'next/navigation';

interface UseCpagelayoutNavigationInput {
  containerRef: RefObject<HTMLElement | null>;
  onLogoClick?: () => void;
}

interface UseCpagelayoutNavigationOutput {
  onLoggedLogoClick: MouseEventHandler<HTMLAnchorElement>;
  onNonLoggedLogoClick: () => void;
  pathname: string | null;
}

export function useCpagelayoutNavigation({
  containerRef,
  onLogoClick,
}: UseCpagelayoutNavigationInput): UseCpagelayoutNavigationOutput {
  const router = useRouter();
  const pathname = usePathname();

  const onLoggedLogoClick = useCallback<MouseEventHandler<HTMLAnchorElement>>((event): void => {
    if (onLogoClick) {
      event.preventDefault();
      onLogoClick();
      return;
    }

    if (pathname === '/') {
      event.preventDefault();
      containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [containerRef, onLogoClick, pathname]);

  const onNonLoggedLogoClick = useCallback((): void => {
    if (pathname === '/') {
      containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    router.push('/');
  }, [containerRef, pathname, router]);

  return {
    onLoggedLogoClick,
    onNonLoggedLogoClick,
    pathname,
  };
}
